import { api, apiRequest } from "./api";
import type {
  BrandMaster,
  APIMaster,
  Prescription,
  PrescriptionItem,
  InvestigationItem,
  InvestigationAdvice,
  CreateMedicineRequest,
  AddPrescriptionItemRequest,
  UpdatePrescriptionItemRequest,
  MedicationDuration,
  MedicationFrequency,
  MedicineUnitMaster,
  DoseFormMaster,
  FollowUp,
  CreateFollowUpRequest,
  UpdateFollowUpRequest,
  OutsideReferral,
  CreateOutsideReferralRequest,
  UpdateOutsideReferralRequest,
  ReferProgram,
  CreateReferProgramRequest,
  UpdateReferProgramRequest,
  Specialist,
} from "@/types/prescription";

export const prescriptionAPI = {
  // Medicines
  searchMedicines: (query: string, doseForm: string = "") =>
    apiRequest<{
      results: BrandMaster[];
      count: number;
      next: string | null;
      previous: string | null;
    }>(() =>
      api.get("/medicines/v2/", {
        params: { search: query, dose_form: doseForm },
      })
    ),

  createMedicine: (data: CreateMedicineRequest) =>
    apiRequest<BrandMaster>(() => api.post("/medicines/v2/", data)),

  // Prescription Management
  getPrescriptions: (appointmentId: string, consolidated: boolean = false) =>
    apiRequest<Prescription[]>(() =>
      api.get(`/medicines/${appointmentId}/prescription/`, {
        params: { consolidated },
      })
    ),

  addPrescriptionItem: (
    appointmentId: string,
    data: AddPrescriptionItemRequest
  ) =>
    apiRequest<Prescription>(() =>
      api.post(`/medicines/${appointmentId}/prescription/`, data)
    ),

  updatePrescriptionItem: (
    appointmentId: string,
    prescriptionItemId: string,
    data: UpdatePrescriptionItemRequest
  ) =>
    apiRequest<PrescriptionItem>(() =>
      api.patch(
        `/medicines/${appointmentId}/prescription-item/${prescriptionItemId}/`,
        data
      )
    ),

  deletePrescriptionItem: (appointmentId: string, prescriptionItemId: string) =>
    apiRequest<void>(() =>
      api.delete(
        `/medicines/${appointmentId}/prescription-item/${prescriptionItemId}/`
      )
    ),

  // Master Data
  getMedicationDurations: () =>
    apiRequest<MedicationDuration[]>(() => api.get("/medicines/duration/")),

  getMedicationFrequencies: () =>
    apiRequest<MedicationFrequency[]>(() => api.get("/medicines/frequency/")),

  getMedicineUnits: () =>
    apiRequest<MedicineUnitMaster[]>(() => api.get("/medicines/v2/unit/")),

  getDoseForms: () =>
    apiRequest<DoseFormMaster[]>(() => api.get("/medicines/v2/dose-forms/")),

  // APIs (Active Pharmaceutical Ingredients)
  searchAPIs: (query: string) =>
    apiRequest<{ results: APIMaster[]; count: number }>(() =>
      api.get("/medicines/v2/api/", { params: { search: query } })
    ),

  // Investigations
  searchInvestigations: (
    query: string,
    type: "lab_tests" | "imaging" | "others" | "" = ""
  ) =>
    apiRequest<InvestigationItem[]>(() =>
      api.get("/investigations/", { params: { search: query, type } })
    ),

  // Investigation Advice
  getInvestigationAdvice: (
    appointmentId: string,
    consolidated: boolean = false
  ) =>
    apiRequest<InvestigationAdvice[]>(() =>
      api.get(`/prescriptions/${appointmentId}/investigation-advice/`, {
        params: { consolidated },
      })
    ),

  addInvestigationAdvice: (
    appointmentId: string,
    investigationItemId: number
  ) =>
    apiRequest<InvestigationAdvice>(() =>
      api.post(`/prescriptions/${appointmentId}/investigation-advice/`, {
        investigation_item: investigationItemId,
      })
    ),

  deleteInvestigationAdviceItem: (
    appointmentId: string,
    adviceItemId: string
  ) =>
    apiRequest<void>(() =>
      api.delete(
        `/prescriptions/${appointmentId}/investigation-advice-item/${adviceItemId}/`
      )
    ),

  // Follow Up
  getFollowUp: (
    appointmentId: string,
    parentType: "prescription" | "meal_plan" = "prescription"
  ) =>
    apiRequest<FollowUp>(() =>
      api.get(`/prescriptions/${appointmentId}/followup/`, {
        params: { parent_type: parentType },
      })
    ),

  createFollowUp: (appointmentId: string, data: CreateFollowUpRequest) =>
    apiRequest<FollowUp>(() =>
      api.post(`/prescriptions/${appointmentId}/followup/`, data)
    ),

  updateFollowUp: (
    appointmentId: string,
    followUpId: string,
    data: UpdateFollowUpRequest
  ) =>
    apiRequest<FollowUp>(() =>
      api.patch(`/prescriptions/${appointmentId}/followup/${followUpId}/`, data)
    ),

  // Outside Referral
  getOutsideReferral: (
    appointmentId: string,
    parentType: "prescription" | "meal_plan" = "prescription"
  ) =>
    apiRequest<OutsideReferral>(() =>
      api.get(`/prescriptions/${appointmentId}/outside-referral/`, {
        params: { parent_type: parentType },
      })
    ),

  createOutsideReferral: (
    appointmentId: string,
    data: CreateOutsideReferralRequest
  ) =>
    apiRequest<OutsideReferral>(() =>
      api.post(`/prescriptions/${appointmentId}/outside-referral/`, data)
    ),

  updateOutsideReferral: (
    appointmentId: string,
    referralId: string,
    data: UpdateOutsideReferralRequest
  ) =>
    apiRequest<OutsideReferral>(() =>
      api.patch(
        `/prescriptions/${appointmentId}/outside-referral/${referralId}/`,
        data
      )
    ),

  // Refer Program
  getReferProgram: (
    appointmentId: string,
    parentType: "prescription" | "meal_plan" = "prescription"
  ) =>
    apiRequest<ReferProgram>(() =>
      api.get(`/prescriptions/${appointmentId}/refer-program/`, {
        params: { parent_type: parentType },
      })
    ),

  createReferProgram: (
    appointmentId: string,
    data: CreateReferProgramRequest
  ) =>
    apiRequest<ReferProgram>(() =>
      api.post(`/prescriptions/${appointmentId}/refer-program/`, data)
    ),

  updateReferProgram: (
    appointmentId: string,
    programId: string,
    data: UpdateReferProgramRequest
  ) =>
    apiRequest<ReferProgram>(() =>
      api.patch(
        `/prescriptions/${appointmentId}/refer-program/${programId}/`,
        data
      )
    ),

  // Specialists
  getSpecialists: () =>
    apiRequest<Specialist[]>(() => api.get("/users/specialists/")),

  // Prescription Preview & Publish
  verifyPrescription: (appointmentId: string) =>
    apiRequest<string>(() =>
      api.get(`/prescriptions/${appointmentId}/verify/`, {
        responseType: "text", // Important: we expect HTML
        headers: {
          Accept: "text/html",
        },
      })
    ),

  publishPrescription: (appointmentId: string) =>
    apiRequest<{ success: boolean }>(() =>
      api.post(`/prescriptions/${appointmentId}/publish/`, {})
    ),

  // Nutrition Report Preview & Publish (for nutritionists)
  verifyNutritionReport: (appointmentId: string) =>
    apiRequest<string>(() =>
      api.get(`/nutrition/${appointmentId}/verify/`, {
        responseType: "text", // Important: we expect HTML
        headers: {
          Accept: "text/html",
        },
      })
    ),

  publishNutritionReport: (appointmentId: string) =>
    apiRequest<{ success: boolean }>(() =>
      api.post(`/nutrition/${appointmentId}/publish/`, {})
    ),
};
